import { isNotDefined, getSourcePathAccessor } from "../utils";
import { SMA as defaultOptions } from "./defaultOptionsForComputation";
import cmo from "./cmo";

/**
 * VMA - Variable Moving Average
 *
 * An adaptive moving average that adjusts its smoothing based on market momentum
 * using the Chande Momentum Oscillator (CMO).
 *
 * Formula:
 * 1. Calculate 9-period CMO
 * 2. VI = abs(CMO) / 100  (Volatility Index, 0 to 1)
 * 3. alpha = 2 / (period + 1)
 * 4. VMA = (alpha * VI * price) + ((1 - alpha * VI) * previousVMA)
 *
 * Characteristics:
 * - More responsive when momentum is high (trending market)
 * - More smooth when momentum is low (ranging market)
 */

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { windowSize, sourcePath } = options;
    const source = getSourcePathAccessor(sourcePath);
    const alpha = 2 / (windowSize + 1);

    // Step 1: Calculate 9-period CMO
    const cmoCalculator = cmo();
    cmoCalculator.options({ windowSize: 9, sourcePath });
    const cmoData = cmoCalculator(data);

    // Step 2: Calculate VMA using CMO as volatility index
    let previousVMA;

    return data.map((d, i) => {
      const v = source(d, i);
      const cmoValue = cmoData[i];

      // Skip undefined values at start
      if (isNotDefined(previousVMA) && isNotDefined(v)) {
        return undefined;
      }

      // Need CMO to calculate VI
      if (isNotDefined(cmoValue)) {
        return undefined;
      }

      // Initialize with first valid price
      if (isNotDefined(previousVMA)) {
        previousVMA = v;
        // Return null for first windowSize bars
        if (i < windowSize - 1) {
          return undefined;
        }
        return v;
      }

      // Calculate Volatility Index (VI) from CMO
      // VI ranges from 0 to 1, higher momentum = higher VI
      const vi = Math.abs(cmoValue) / 100;

      // Calculate VMA using adaptive alpha
      const adaptiveAlpha = alpha * vi;
      const nextValue = adaptiveAlpha * v + (1 - adaptiveAlpha) * previousVMA;
      previousVMA = nextValue;

      // Return null for initialization period
      if (i < windowSize - 1) {
        return undefined;
      }

      return nextValue;
    });
  }

  calculator.undefinedLength = function () {
    const { windowSize } = options;
    // Need max of windowSize and CMO period (9) + 1
    return Math.max(windowSize - 1, 9);
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
